import { useEffect, useId, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { feedbackService } from '@/services';
import {
  FEEDBACK_CATEGORIES,
  type FeedbackCategory,
  type SubmitFeedbackInput,
} from '@/services/feedback.service';
import { collectFeedbackContext } from '@/lib/feedbackContext';
import { captureFeedbackEvent } from '@/lib/sentry';
import { useUserStore } from '@/stores/userStore';
import { useFeedbackStore } from '@/stores/feedbackStore';

// The floor is the server's (edge function + the table's CHECK), not a
// design choice: letting Send fire under it would surface our "on our end"
// error for what is really a too-short message.
const MIN_MESSAGE_LENGTH = 10;
const MAX_MESSAGE_LENGTH = 2000;

/**
 * Parents' words, not ours (YAR-483, approved at Meeting #147). The wire
 * values are unchanged: `bug` · `feature` · `general` — "Other" maps to
 * `general`, the catch-all the table's CHECK constraint already accepts.
 */
const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: 'Not working',
  feature: 'Idea',
  general: 'Other',
};

/** The field's prompt follows the category (YAR-483). */
const FEEDBACK_PROMPTS: Record<FeedbackCategory, string> = {
  bug: 'What happened? What did you expect?',
  feature: 'What would make Yaruta more useful for you?',
  general: 'What would you like us to know?',
};
const FEEDBACK_DEFAULT_PROMPT = 'Share your thoughts, issues, or ideas.';

const FEEDBACK_ERROR_COPY = 'Something went wrong on our end. Try again.';
const FEEDBACK_SENT_COPY =
  'Thanks for your feedback. We may email you if we have questions.';

type Phase = 'form' | 'submitting' | 'success';

export interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Override the submit handler. The default invokes
   * `feedbackService.submitFeedback`. Tests and Storybook stories use this
   * to simulate loading/success/error without hitting the network.
   */
  onSubmit?: (input: SubmitFeedbackInput) => Promise<void>;
  /**
   * Test/Storybook only — drives the modal into a non-form phase so the
   * post-submit visual states are inspectable without actually submitting.
   * Production callers should never set this; it bypasses the user flow.
   */
  _initialPhase?: Phase;
  /**
   * Test/Storybook only — seeds the draft's category on open. Production
   * callers leave it unset so the kept draft (YAR-483) comes back instead.
   */
  _initialCategory?: FeedbackCategory;
  /** Test/Storybook only — seeds the draft's message. See `_initialCategory`. */
  _initialMessage?: string;
  /** Test/Storybook only — shows the error line without submitting. */
  _initialError?: boolean;
}

export function FeedbackModal({
  open,
  onClose,
  onSubmit,
  _initialPhase = 'form',
  _initialCategory,
  _initialMessage,
  _initialError = false,
}: FeedbackModalProps) {
  const [phase, setPhase] = useState<Phase>(_initialPhase);
  const [error, setError] = useState(_initialError);
  // The draft lives in the store, not here: it has to outlive this modal
  // closing (YAR-483 — an accidental close keeps the text and category).
  const category = useFeedbackStore((s) => s.category);
  const message = useFeedbackStore((s) => s.message);
  const setCategory = useFeedbackStore((s) => s.setCategory);
  const setMessage = useFeedbackStore((s) => s.setMessage);
  const clearDraft = useFeedbackStore((s) => s.clearDraft);

  const location = useLocation();
  const timezone = useUserStore((s) => s.timezone);

  const baseId = useId();
  const messageId = `${baseId}-message`;
  const hintId = `${baseId}-hint`;
  const categoryLabelId = `${baseId}-category-label`;

  // Every open starts in the form with no error line; only the draft
  // carries over from the last time.
  useEffect(() => {
    if (!open) return;
    setPhase(_initialPhase);
    setError(_initialError);
    if (_initialCategory !== undefined) setCategory(_initialCategory);
    if (_initialMessage !== undefined) setMessage(_initialMessage);
  }, [
    open,
    _initialPhase,
    _initialCategory,
    _initialMessage,
    _initialError,
    setCategory,
    setMessage,
  ]);

  // Validation runs on the trimmed message — that is what gets sent, and
  // what the DB CHECK sees.
  const trimmedLength = message.trim().length;
  const overLimit = trimmedLength > MAX_MESSAGE_LENGTH;
  const tooShort = trimmedLength > 0 && trimmedLength < MIN_MESSAGE_LENGTH;
  const canSubmit =
    category !== null &&
    trimmedLength >= MIN_MESSAGE_LENGTH &&
    !overLimit &&
    phase === 'form';

  const handleSubmit = async () => {
    if (!canSubmit || category === null) return;
    setPhase('submitting');
    setError(false);
    try {
      const trimmed = message.trim();
      // Best-effort, and deliberately inside the try with its own guard: a
      // thrown collector must not cost the user their report.
      let context;
      try {
        const sentryEventId = captureFeedbackEvent(category, trimmed);
        context = collectFeedbackContext(
          location.pathname,
          timezone,
          sentryEventId,
        );
      } catch (contextErr) {
        console.error('Failed to collect feedback context', contextErr);
        context = undefined;
      }

      const submit = onSubmit ?? feedbackService.submitFeedback;
      await submit({ category, message: trimmed, context });
      clearDraft();
      setPhase('success');
    } catch (err) {
      // User-safe line only; the real error goes to the console for us.
      // The text stays, and Send retries (YAR-484 AC 8).
      console.error('Failed to submit feedback', err);
      setError(true);
      setPhase('form');
    }
  };

  const title = (
    <h2 className="m-0 text-xl font-medium leading-7 text-text-heading">
      Send feedback
    </h2>
  );

  // Sent — Figma Settings / Send Feedback / sent (11066:178134): title, the
  // line, and a tertiary Close. No icon.
  if (phase === 'success') {
    return (
      <Modal
        open={open}
        onClose={onClose}
        ariaLabel="Send feedback"
        size="md"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5 text-center">
            {title}
            <p
              role="status"
              className="m-0 text-base font-normal leading-6 text-text-body"
            >
              {FEEDBACK_SENT_COPY}
            </p>
          </div>
          <Button
            variant="tertiary"
            size="md"
            className="w-full"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  const submitting = phase === 'submitting';
  const placeholder =
    category === null ? FEEDBACK_DEFAULT_PROMPT : FEEDBACK_PROMPTS[category];
  const fieldInvalid = error || overLimit;
  // One line under the field, error first (Figma / error 11066:178372).
  const hint = error
    ? FEEDBACK_ERROR_COPY
    : overLimit
      ? `${MAX_MESSAGE_LENGTH} characters maximum`
      : tooShort
        ? `${MIN_MESSAGE_LENGTH} characters minimum`
        : '';

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      ariaLabel="Send feedback"
      size="md"
    >
      {/* Figma Settings / Send Feedback (.modal, Type=text area). */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5 text-center">
          {title}
          <p className="m-0 text-base font-normal leading-6 text-text-body">
            Tell us what&rsquo;s working, what&rsquo;s confusing, or what you
            wish Yaruta did better.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <span
            id={categoryLabelId}
            className="text-sm font-medium leading-5 text-text-body"
          >
            Category
          </span>
          {/* Single-select group — radiogroup is the correct pattern for
              pill-style chips. The selected chip gets the `.filter chip`
              selected treatment: the same Badge with a
              `surface/neutral/default-hover` (#e5e5e5) fill. */}
          <div
            role="radiogroup"
            aria-labelledby={categoryLabelId}
            className="flex flex-wrap gap-2"
          >
            {FEEDBACK_CATEGORIES.map((c) => {
              const selected = category === c;
              return (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={submitting}
                  // Switching category never touches the text (YAR-483).
                  onClick={() => setCategory(c)}
                  className={[
                    'inline-flex shrink-0 items-center justify-center rounded-full border border-border-strong px-2.5 py-1.5',
                    'text-base font-normal leading-6 text-text-body whitespace-nowrap cursor-pointer',
                    'transition-colors duration-150 disabled:cursor-not-allowed',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                    selected
                      ? 'bg-surface-strong'
                      : 'bg-background hover:bg-surface-secondary',
                  ].join(' ')}
                >
                  {FEEDBACK_CATEGORY_LABELS[c]}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          {/* The visible label stays — never placeholder-only (YAR-483). */}
          <label
            htmlFor={messageId}
            className="text-sm font-medium leading-5 text-text-body"
          >
            Your feedback
          </label>
          <textarea
            id={messageId}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={submitting}
            rows={5}
            // A small overshoot so the over-limit line can appear and the
            // user sees the 2,000 limit instead of being silently capped.
            // No visible counter: Figma draws none (YAR-482 open question).
            maxLength={MAX_MESSAGE_LENGTH + 100}
            placeholder={placeholder}
            aria-describedby={hintId}
            aria-invalid={fieldInvalid ? true : undefined}
            className={[
              'w-full min-h-[118px] resize-y rounded-md border bg-white px-3 py-2',
              'text-sm leading-5 text-text-body',
              'placeholder:text-text-placeholder',
              'focus:outline-2 focus:outline-offset-0 focus:outline-border-focus',
              'disabled:bg-disabled-bg disabled:text-disabled-text disabled:cursor-not-allowed',
              'transition-colors duration-150',
              fieldInvalid ? 'border-border-error' : 'border-border',
            ].join(' ')}
          />
          {/* Always mounted so aria-describedby resolves; collapses when
              empty. The error line blames the system, never the parent. */}
          <p
            id={hintId}
            role={error ? 'alert' : undefined}
            aria-live={error ? undefined : 'polite'}
            className={[
              'm-0 text-sm leading-5 empty:hidden',
              fieldInvalid ? 'text-text-error' : 'text-text-caption',
            ].join(' ')}
          >
            {hint}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            variant="primary"
            size="md"
            className="w-full"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting}
          >
            Send feedback
          </Button>
          {/* `tertiary`, not `ghost`: Figma's Cancel is
              `text/neutral/default` #5e5e5e (YAR-230). */}
          <Button
            variant="tertiary"
            size="md"
            className="w-full"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
